<template>
  <section class="sector-page">
    <h2 class="sector-page__title">{{ $t('chpo.sectors.title') }}</h2>
    <div class="sector-page__subtitle">{{ $t('chpo.sectors.subtitle') }}</div>

    <div class="sector-map">
      <div
          id="sector-map"
          class="sector-map__viewport"
          role="application"
          :aria-label="$t('chpo.sectors.mapAria')"
      ></div>
    </div>

    <div class="sector-actions">
      <ActionButton :outlined="true" :borderColor="'#111827'" :text="$t('chpo.sectors.close')" @click="onClose" />
      <ActionButton
          :color="'#2A8A1B'"
          :disabled="!selectedSectors.length"
          :text="selectedButtonText"
          @click="onNext"
      />
    </div>
  </section>
</template>
<script>
import ActionButton from '@/components/widgets/ActionButton.vue'
import L from 'leaflet'
import { createFlatMap, addGrid } from '@/components/maps/map.js'
import { listAllMapMarkers } from '@/api/chpoMap'

// === иконки как в обновлённом ChpoMap ===
const ICON_FILES = {
  'marker1.svg': require('@/assets/images/map/marker1.svg'),
  'camera.svg':  require('@/assets/images/map/camera.svg'),
  'radar.svg':   require('@/assets/images/map/radar.svg'),
  'vehicle.svg': require('@/assets/images/map/vehicle.svg'),
  'dog.svg':     require('@/assets/images/map/dog.svg'),
  'tower.svg':   require('@/assets/images/map/tower.svg'),
}
const ICON_KEY_TO_FILE = {
  post: 'marker1.svg',
  camera: 'camera.svg',
  radar: 'radar.svg',
  vehicle: 'vehicle.svg',
  dog: 'dog.svg',
  tower: 'tower.svg',
}
const PNG_TO_SVG = {
  'marker1.png': 'marker1.svg',
  'camera.png':  'camera.svg',
  'radar.png':   'radar.svg',
  'vehicle.png': 'vehicle.svg',
  'dog.png':     'dog.svg',
  'tower.png':   'tower.svg',
}

let MAP_ICONS_CTX
let MAP_ICON_KEYS = new Set()
try {
  MAP_ICONS_CTX = require.context('@/assets/images/map/map_icons', false, /\.(png|jpe?g|svg|bmp)$/i)
  MAP_ICON_KEYS = new Set(MAP_ICONS_CTX.keys().map(k => k.replace('./', '')))
} catch (e) {
  // eslint-disable-next-line no-console
  console.warn('SectorSelect: map_icons недоступны:', e)
}

function resolveIconSrc(nameOrKey) {
  if (!nameOrKey) return ICON_FILES['marker1.svg']

  if (/\.(svg|png|jpe?g|bmp)$/i.test(nameOrKey)) {
    const fname = nameOrKey
    if (MAP_ICONS_CTX && MAP_ICON_KEYS.has(fname)) {
      return MAP_ICONS_CTX('./' + fname)
    }
    const mapped = PNG_TO_SVG[fname]
    if (mapped && ICON_FILES[mapped]) return ICON_FILES[mapped]
    if (ICON_FILES[fname]) return ICON_FILES[fname]
    return ICON_FILES['marker1.svg']
  }

  const file = ICON_KEY_TO_FILE[nameOrKey] || 'marker1.svg'
  return ICON_FILES[file] || ICON_FILES['marker1.svg']
}

export default {
  name: 'SectorSelectView',
  components: { ActionButton },
  data() {
    return {
      selectedSectors: [],
      rowId: null,
      backPath: null,

      gridCols: 20,
      gridRows: 17,
      gridLetters: ['А','Б','В','Г','Д','Е','Ж','З','И','К','Л','М', 'Н', 'О', 'П', 'Р', 'С', 'Т', 'У', 'Ф', 'Х', 'Ц', 'Ч', 'Ш', 'Щ', 'Ъ', 'Ы', 'Ь', 'Э', 'Ю', 'Я'],

      map: null,
      bounds: null,
      highlightLayer: null,
      highlightRects: [],
      markerLayer: null,

      MAX_SAFE: 999.999,
      scaleX: 1,
      scaleY: 1,
    }
  },
  computed: {
    selectedButtonText() {
      if (!this.selectedSectors.length) return this.$t('chpo.sectors.next')
      if (this.selectedSectors.length === 1) return this.$t('chpo.sectors.nextOne', { code: this.selectedSectors[0] })
      return this.$t('chpo.sectors.nextMany', { n: this.selectedSectors.length })
    },
  },
  created() {
    const { rowId, back, sectorSelected, sectorsSelected } = this.$route.query || {}
    this.rowId = rowId ? Number(rowId) : null
    this.backPath = back ? String(back) : '/chpo/plan'

    const codes = []
    if (Array.isArray(sectorsSelected)) {
      sectorsSelected.forEach(s => typeof s === 'string' && codes.push(s.toUpperCase().trim()))
    } else if (typeof sectorsSelected === 'string' && sectorsSelected) {
      codes.push(
        ...sectorsSelected
          .split(',')
          .map(s => s.toUpperCase().trim())
          .filter(Boolean),
      )
    } else if (typeof sectorSelected === 'string' && sectorSelected) {
      codes.push(sectorSelected.toUpperCase().trim())
    }
    this.selectedSectors = [...new Set(codes)]
  },
  mounted() {
    this.setupMap()
  },
  methods: {
    parseSectorCode(code) {
      if (!code || typeof code !== 'string') return null
      const upper = code.toUpperCase().trim()
      const letter = upper[0]
      const numberPart = upper.slice(1)
      const colIdx = this.gridLetters.indexOf(letter)
      const num = Number(numberPart)
      if (colIdx === -1 || !Number.isInteger(num) || num < 1 || num > this.gridRows) return null
      return { colIdx, rowIdx: num - 1 }
    },

    async setupMap() {
      try {
        // та же картинка, что и в новом ChpoMap
        const imageUrl = require('@/assets/images/map/maps/mapdps1.png')

        const { map, bounds } = await createFlatMap('sector-map', imageUrl, {
          zoomControl: true,
        })
        this.map = map
        this.bounds = bounds

        // центрируем и вписываем карту
        map.fitBounds(bounds, { animate: false })

        // базовый масштаб
        const initialZoom = map.getZoom()
        map.setMinZoom(initialZoom)
        map.setMaxZoom(initialZoom + 4)

        map.setMaxBounds(bounds)
        map.options.maxBoundsViscosity = 1.0

        map.dragging.enable()
        map.scrollWheelZoom.enable()
        map.touchZoom.enable()
        map.doubleClickZoom.enable()
        map.boxZoom.enable()
        map.keyboard.enable()

        const east = bounds.getEast()
        const north = bounds.getNorth()
        this.scaleX = east > this.MAX_SAFE ? (this.MAX_SAFE / east) : 1
        this.scaleY = north > this.MAX_SAFE ? (this.MAX_SAFE / north) : 1

        addGrid(map, bounds, this.gridCols, this.gridRows)

        this.highlightLayer = L.layerGroup().addTo(map)
        this.markerLayer = L.layerGroup().addTo(map)

        map.on('click', this.onMapClick)

        // подсветка выбранных из query
        this.selectedSectors.forEach(code => {
          const parsed = this.parseSectorCode(code)
          if (parsed) this.drawHighlight(parsed.colIdx, parsed.rowIdx, code)
        })

        // await this.loadMarkers()
      } catch (e) {
        console.error('SectorSelect: init map failed', e)
      }
    },

    async loadMarkers() {
      try {
        const list = await listAllMapMarkers().catch(() => [])
        if (Array.isArray(list) && this.markerLayer) {
          list.forEach(this.drawExistingMarker)
        }
      } catch (e) {
        console.warn('SectorSelect: load markers failed', e)
      }
    },

    drawExistingMarker(rec) {
      if (!this.markerLayer || !this.bounds) return
      const rawLat = Number(parseFloat(rec.lat).toFixed(6))
      const rawLng = Number(parseFloat(rec.lng).toFixed(6))
      if (!isFinite(rawLat) || !isFinite(rawLng)) return

      const south = this.bounds.getSouth()
      const west = this.bounds.getWest()
      const north = this.bounds.getNorth()
      const east = this.bounds.getEast()

      let lat = rawLat
      let lng = rawLng
      const needsUnscale =
        (east > this.MAX_SAFE || north > this.MAX_SAFE) &&
        rawLat <= this.MAX_SAFE &&
        rawLng <= this.MAX_SAFE

      if (needsUnscale) {
        lat = rawLat / (this.scaleY || 1)
        lng = rawLng / (this.scaleX || 1)
      }

      if (lat < south || lat > north || lng < west || lng > east) return

      const nameOrKey = rec.img_name || rec.key || rec.icon_key
      const src = resolveIconSrc(nameOrKey)
      const icon = L.divIcon({
        className: 'map-marker',
        html: `<img src="${src}" alt="marker" />`,
        iconSize: [24, 24],
        iconAnchor: [12, 12],
      })
      L.marker([lat, lng], { icon, keyboard: false }).addTo(this.markerLayer)
    },

    onMapClick(evt) {
      if (!this.map || !this.bounds) return
      const lat = evt.latlng.lat
      const lng = evt.latlng.lng
      const south = this.bounds.getSouth()
      const west = this.bounds.getWest()
      const north = this.bounds.getNorth()
      const east = this.bounds.getEast()
      if (lat < south || lat > north || lng < west || lng > east) return

      const width = east - west
      const height = north - south
      const colW = width / this.gridCols
      const rowH = height / this.gridRows

      let colIdx = Math.floor((lng - west) / colW)
      let rowIdx = Math.floor((lat - south) / rowH)

      if (colIdx >= this.gridCols) colIdx = this.gridCols - 1
      if (rowIdx >= this.gridRows) rowIdx = this.gridRows - 1
      if (colIdx < 0) colIdx = 0
      if (rowIdx < 0) rowIdx = 0

      const letter = this.gridLetters[colIdx] || ''
      const number = rowIdx + 1
      const code = `${letter}${number}`

      const idx = this.selectedSectors.indexOf(code)
      if (idx === -1) {
        this.selectedSectors.push(code)
      } else {
        this.selectedSectors.splice(idx, 1)
      }
      this.redrawHighlights()
    },

    redrawHighlights() {
      if (!this.highlightLayer || !this.bounds) return
      this.highlightRects.forEach(r => this.highlightLayer.removeLayer(r))
      this.highlightRects = []
      this.selectedSectors.forEach(code => {
        const parsed = this.parseSectorCode(code)
        if (!parsed) return
        const rect = this.drawHighlight(parsed.colIdx, parsed.rowIdx, code)
        if (rect) this.highlightRects.push(rect)
      })
    },

    drawHighlight(colIdx, rowIdx) {
      if (!this.highlightLayer || !this.bounds) return null
      const south = this.bounds.getSouth()
      const west = this.bounds.getWest()
      const north = this.bounds.getNorth()
      const east = this.bounds.getEast()
      const width = east - west
      const height = north - south
      const colW = width / this.gridCols
      const rowH = height / this.gridRows

      const cellSouth = south + rowIdx * rowH
      const cellNorth = cellSouth + rowH
      const cellWest = west + colIdx * colW
      const cellEast = cellWest + colW

      const rect = L.rectangle(
        [
          [cellSouth, cellWest],
          [cellNorth, cellEast],
        ],
        {
          color: '#2A8A1B',
          weight: 2,
          fill: true,
          fillColor: '#2A8A1B',
          fillOpacity: 0.12,
          interactive: false,
        },
      ).addTo(this.highlightLayer)

      return rect
    },

    onClose() {
      if (this.backPath) {
        this.$router.push(this.backPath)
      } else {
        this.$router.back()
      }
    },

    onNext() {
      if (!this.selectedSectors.length) return
      const target = this.parseBackToRoute(this.backPath)
      target.query = {
        ...(target.query || {}),
        sectorsSelected: this.selectedSectors.join(','),
        rowId: String(this.rowId ?? ''),
      }
      this.$router.push(target)
    },

    parseBackToRoute(back) {
      if (!back) return { path: '/chpo/plan', query: {} }
      const qIndex = back.indexOf('?')
      if (qIndex === -1) return { path: back, query: {} }
      const path = back.slice(0, qIndex)
      const qs = back.slice(qIndex + 1)
      const sp = new URLSearchParams(qs)
      const query = {}
      sp.forEach((v, k) => {
        query[k] = v
      })
      return { path, query }
    },
  },
}
</script>


<style scoped>
.sector-page {
  background: #fff;
  border-radius: 16px;
  padding: 12px;
  display: grid;
  grid-template-rows: auto auto 1fr auto;
  gap: 12px;
}
.sector-page__title {
  color: #111827;
  font-size: 24px;
  font-weight: 700;
  line-height: 31.2px;
  margin: 0;
  text-align: center;
}
.sector-page__subtitle {
  color: #111827;
  font-size: 20px;
  font-weight: 600;
  line-height: 30px;
  letter-spacing: 0.3px;
  text-align: center;
}

.sector-map {
  position: relative;
  width: 80%;
  margin: 0 auto;
  min-height: 480px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
}
.sector-map__viewport {
  position: absolute;
  inset: 0;
}

/* маркеры */
.map-marker img {
  width: 24px;
  height: 24px;
  display: block;
}

.marker-label {
  position: absolute;
  transform: translate(-50%, -50%);
  text-align: center;
  color: #000;
}
.marker-label__code {
  font-size: 10px;
  font-weight: 600;
}
.marker-label__note {
  font-size: 8px;
  line-height: 12px;
}

.sector-actions {
  display: flex;
  justify-content: center;
  gap: 16px;
}

:deep(.leaflet-control-attribution) {
  display: none !important;
}
</style>
